/* =========================================================================
   Volition product page behaviour.

   Deliberately dependency-free. The handover drives its gallery with Swiper and
   its variant logic with Alpine, neither of which this theme carries, and the
   gallery is a plain horizontal snap scroller once you strip the library. Every
   handler below no-ops when its markup is absent so the script is safe on any
   template that happens to load it.
   ========================================================================= */
(function () {
  "use strict";

  function gallery(root) {
    var track = root.querySelector("[data-vpdp-track]");
    if (!track) return;

    var slides = Array.prototype.slice.call(track.querySelectorAll(".vpdp-slide"));
    var counter = root.querySelector("[data-vpdp-count]");
    var prev = root.querySelector(".vpdp-arrow.prev");
    var next = root.querySelector(".vpdp-arrow.next");
    if (slides.length < 2) {
      if (prev) prev.hidden = true;
      if (next) next.hidden = true;
    }

    function index() {
      // Round rather than floor: a snapped slide can sit a fraction of a pixel
      // off and floor would report the previous one.
      return Math.round(track.scrollLeft / track.clientWidth);
    }

    function paint() {
      var i = Math.min(Math.max(index(), 0), slides.length - 1);
      if (counter) counter.textContent = i + 1 + " / " + slides.length;
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
    }

    function go(delta) {
      track.scrollTo({ left: (index() + delta) * track.clientWidth, behavior: "smooth" });
    }

    if (prev) prev.addEventListener("click", function () { go(-1); });
    if (next) next.addEventListener("click", function () { go(1); });

    var queued = false;
    track.addEventListener(
      "scroll",
      function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          paint();
        });
      },
      { passive: true }
    );

    // Clicking a colour swatch jumps the gallery to that variant's image.
    root.addEventListener("click", function (e) {
      var swatch = e.target.closest ? e.target.closest("[data-vpdp-goto]") : null;
      if (!swatch) return;
      var target = parseInt(swatch.getAttribute("data-vpdp-goto"), 10);
      if (isNaN(target)) return;
      track.scrollTo({ left: target * track.clientWidth, behavior: "smooth" });
    });

    window.addEventListener("resize", paint);
    paint();
  }

  function variants(root) {
    var form = root.querySelector("[data-vpdp-form]");
    if (!form) return;

    var input = form.querySelector("[data-vpdp-variant]");
    var atc = form.querySelector(".vpdp-atc");
    var buttons = Array.prototype.slice.call(root.querySelectorAll("[data-vpdp-size]"));
    var valueLabel = root.querySelector("[data-vpdp-optvalue]");
    if (!buttons.length) return;

    function select(button) {
      if (button.getAttribute("data-available") === "false") return;

      buttons.forEach(function (b) {
        b.setAttribute("aria-checked", String(b === button));
      });
      if (input) input.value = button.getAttribute("data-variant-id") || "";
      if (valueLabel) valueLabel.textContent = button.getAttribute("data-value") || "";
      if (atc) {
        atc.disabled = false;
        var label = atc.getAttribute("data-label-default");
        if (label) atc.textContent = label;
      }

      // Stock line follows the selected size (state + text from the button).
      var stock = root.querySelector("[data-vpdp-stock]");
      if (stock) {
        var state = button.getAttribute("data-stock-state");
        var text = button.getAttribute("data-stock-text");
        if (state) {
          stock.classList.remove("is-in", "is-low", "is-out");
          stock.classList.add("is-" + state);
        }
        var textEl = stock.querySelector("[data-vpdp-stock-text]");
        if (textEl && text) textEl.textContent = text;
      }
    }

    buttons.forEach(function (button) {
      button.addEventListener("click", function () {
        select(button);
      });
    });

    // Preselect the first purchasable size so Add to cart is never a dead end.
    var initial = buttons.filter(function (b) {
      return b.getAttribute("data-available") !== "false";
    })[0];
    if (initial) {
      select(initial);
    } else if (atc) {
      atc.disabled = true;
      var soldOut = atc.getAttribute("data-label-soldout");
      if (soldOut) atc.textContent = soldOut;
    }
  }

  /* Single-open accordion group. Also used for the shipping/duties rows, which
     behave the same way in the handover. */
  function disclosures(root) {
    var groups = Array.prototype.slice.call(root.querySelectorAll("[data-vpdp-disclosure]"));
    groups.forEach(function (group) {
      var tabs = Array.prototype.slice.call(group.querySelectorAll("[data-vpdp-toggle]"));
      tabs.forEach(function (tab) {
        tab.addEventListener("click", function () {
          var panel = group.querySelector("#" + tab.getAttribute("aria-controls"));
          if (!panel) return;
          var willOpen = panel.hidden;

          if (group.getAttribute("data-vpdp-disclosure") === "single") {
            tabs.forEach(function (other) {
              var p = group.querySelector("#" + other.getAttribute("aria-controls"));
              if (!p) return;
              p.hidden = true;
              other.setAttribute("aria-expanded", "false");
              var s = other.querySelector(".sign");
              if (s) s.textContent = "+";
            });
          }

          panel.hidden = !willOpen;
          tab.setAttribute("aria-expanded", String(willOpen));
          var sign = tab.querySelector(".sign");
          if (sign) sign.textContent = willOpen ? "\u2212" : "+";
        });
      });
    });
  }

  /* "Order within HH:MM:SS for next day delivery" — counts down to a cutoff
     time each day rather than a fixed date, so it never expires. */
  function cutoff(root) {
    var el = root.querySelector("[data-vpdp-cutoff]");
    if (!el) return;
    var hour = parseInt(el.getAttribute("data-vpdp-cutoff"), 10);
    if (isNaN(hour)) return;

    function pad(n) {
      return (n < 10 ? "0" : "") + n;
    }

    function tick() {
      var now = new Date();
      var target = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, 0, 0);
      if (target <= now) target.setDate(target.getDate() + 1);
      var s = Math.floor((target - now) / 1000);
      el.textContent =
        pad(Math.floor(s / 3600)) + ":" + pad(Math.floor((s % 3600) / 60)) + ":" + pad(s % 60);
    }

    tick();
    setInterval(tick, 1000);
  }

  /* Native `popover` handles open/close without JS. This only locks body
     scroll while open, and is a no-op when popover is unsupported. */
  function sizeChart() {
    var dialogs = document.querySelectorAll("[data-vpdp-sizechart][popover]");
    if (!dialogs.length) return;
    Array.prototype.forEach.call(dialogs, function (dialog) {
      dialog.addEventListener("toggle", function (e) {
        if (e.newState === "open") {
          document.body.style.overflow = "hidden";
        } else {
          document.body.style.overflow = "";
        }
      });
    });
  }

  function init() {
    var roots = document.querySelectorAll(".vpdp");
    Array.prototype.forEach.call(roots, function (root) {
      gallery(root);
      variants(root);
      disclosures(root);
      cutoff(root);
    });
    sizeChart();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
